/**
 * @fileoverview Auth-shell strings barrel + `loadAuthShellStrings(i18nInstance, locales)`.
 * @package @puffin-core/auth-shell
 *
 * Mirrors `@puffin-core/login-shell`'s `loadLoginStrings` and `@puffin-core/page-shell`'s
 * `loadShellStrings`. Consumers should NOT call this by hand — the descriptor in
 * `@puffin-core/i18n-namespaces` registers it for every app at boot (ruling 39), which is what stops
 * app six from forgetting.
 */

import { DEFAULT_AUTH_SHELL_STRINGS } from './en.js';
import { AUTH_SHELL_STRINGS_ES } from './es.js';
import { AUTH_SHELL_NAMESPACE } from './namespace.js';

/** The English bundle under a symmetric name. Same frozen object as the default export. */
export const AUTH_SHELL_STRINGS_EN = DEFAULT_AUTH_SHELL_STRINGS;

const BUNDLES = Object.freeze({
  en: AUTH_SHELL_STRINGS_EN,
  es: AUTH_SHELL_STRINGS_ES,
});

/** Locales this package ships. `createAppI18n` derives its list from these keys. */
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register the auth-shell bundles on a react-i18next instance.
 *
 * @param {Object} i18nInstance - a react-i18next instance
 * @param {string|string[]} [locales] - locale or locales to register
 * @returns {Object} the same instance, for chaining
 */
export function loadAuthShellStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error(
      'loadAuthShellStrings: expected a react-i18next instance with addResourceBundle()',
    );
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadAuthShellStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Deep merge, do NOT overwrite — an app that has already injected its own
    // `authShell.*` override keeps it.
    i18nInstance.addResourceBundle(locale, AUTH_SHELL_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

export { DEFAULT_AUTH_SHELL_STRINGS, AUTH_SHELL_STRINGS_ES, AUTH_SHELL_NAMESPACE };
export { resolveString } from './resolveString.js';
